import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, ShieldCheck, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?fm=jpg&q=85&w=2400&auto=format&fit=crop&ixlib=rb-4.1.0";

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Modern custom home exterior"
          className="w-full h-full object-cover animate-slow-zoom"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-32 lg:pt-44 lg:pb-48">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9">
            <div
              className="inline-flex items-center gap-3 mb-10 animate-fade-up"
              data-testid="hero-badge"
            >
              <span className="w-10 h-px bg-[#B08D5B]" />
              <MapPin className="w-3.5 h-3.5 text-[#D4B888]" />
              <span className="label-overline text-[10px] text-[#D4B888]">
                Greater Toronto Area · Est. 2012
              </span>
            </div>

            <h1
              className="font-heading text-[44px] sm:text-6xl lg:text-[88px] text-white leading-[1.02] font-light tracking-tight animate-fade-up"
              style={{ animationDelay: "0.1s" }}
              data-testid="hero-headline"
            >
              Transforming
              <br />
              houses into{" "}
              <span className="italic text-[#D4B888] font-normal">dream homes</span>
              <br className="hidden sm:block" />
              <span className="hidden sm:inline">across the GTA.</span>
              <span className="sm:hidden"> across the GTA.</span>
            </h1>

            <p
              className="mt-10 text-base sm:text-lg font-body text-stone-200/85 max-w-xl leading-[1.8] animate-fade-up"
              style={{ animationDelay: "0.25s" }}
              data-testid="hero-subheadline"
            >
              An architectural-grade renovation studio specialising in custom homes, legal
              basements and luxury kitchen & bath remodels — built end-to-end by licensed
              tradespeople.
            </p>

            <div
              className="mt-12 flex flex-wrap items-center gap-4 animate-fade-up"
              style={{ animationDelay: "0.4s" }}
            >
              <Button
                asChild
                className="h-13 px-8 py-3.5 bg-[#B08D5B] hover:bg-[#8A6B3F] text-white font-medium rounded-none transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-15px_rgba(176,141,91,0.6)] tracking-wide text-[13px] uppercase"
                data-testid="hero-cta-quote"
              >
                <a href="#contact" className="inline-flex items-center gap-3">
                  Request a Quote
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="ghost"
                className="h-13 px-8 py-3.5 bg-transparent hover:bg-white/5 border border-white/30 hover:border-white/60 text-white hover:text-white rounded-none font-medium transition-all duration-500 tracking-wide text-[13px] uppercase"
                data-testid="hero-cta-call"
              >
                <a href="tel:+14168264590" className="inline-flex items-center gap-3">
                  <Phone className="w-4 h-4" />
                  Call Us Now · Free Estimates
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-3 animate-fade-up" style={{ animationDelay: "0.55s" }}>
            <div className="flex lg:flex-col gap-6 lg:gap-5 lg:border-l lg:border-[#B08D5B]/40 lg:pl-6">
              <div data-testid="hero-stat-projects">
                <div className="font-heading text-4xl lg:text-5xl text-[#D4B888] font-light">
                  500+
                </div>
                <div className="label-overline text-[10px] text-stone-300 mt-1">
                  Projects Delivered
                </div>
              </div>
              <div data-testid="hero-stat-years">
                <div className="font-heading text-4xl lg:text-5xl text-[#D4B888] font-light">
                  12<span className="text-2xl">yrs</span>
                </div>
                <div className="label-overline text-[10px] text-stone-300 mt-1">
                  In The GTA
                </div>
              </div>
              <div data-testid="hero-stat-insured" className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#D4B888]" />
                <div>
                  <div className="font-heading text-base text-white">Licensed</div>
                  <div className="label-overline text-[9px] text-stone-300 mt-0.5">
                    & Fully Insured
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decorative rule */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B08D5B]/50 to-transparent" />
    </section>
  );
}
